/**
 * BuildPanel - UI panel for building
 */

import { UIPanel } from '../ui/UIPanel';
import { Vector2 } from '../math/Vector2';
import { BuildingSystem } from './BuildingSystem';
import { InventoryComponent } from '../items/InventoryComponent';
import { Blueprint } from './Structure';
import { ItemRegistry } from '../items/ItemRegistry';
import { t, Localization } from '../systems/Localization';

export class BuildPanel extends UIPanel {
  private buildingSystem: BuildingSystem;
  private inventory: InventoryComponent | null = null;
  private selectedBlueprint: Blueprint | null = null;
  private blueprints: Blueprint[] = [];

  private slotSize: number = 60;
  private slotsPerRow: number = 3;
  private hoveredSlot: number = -1;

  getSelectedBlueprint(): Blueprint | null {
    return this.selectedBlueprint;
  }

  constructor(buildingSystem: BuildingSystem) {
    super(0, 0, 250, 380);
    this.isDraggable = true;
    this.buildingSystem = buildingSystem;
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  setBuildingSystem(buildingSystem: BuildingSystem): void {
    this.buildingSystem = buildingSystem;
  }

  show(): void {
    super.show();
    this.title = t('panel_build');
    this.blueprints = this.buildingSystem.getAllBlueprints();
  }

  protected onPanelClick(pos: Vector2): boolean {
    const slot = this.getSlotAtPosition(pos);
    if (slot >= 0 && slot < this.blueprints.length) {
      const blueprint = this.blueprints[slot];
      if (blueprint) {
        this.selectedBlueprint = blueprint;
        this.buildingSystem.enterBuildMode(blueprint.id);
        this.hide();
      }
      return true;
    }
    return false;
  }

  handleMouseMove(pos: Vector2): boolean {
    if (super.handleMouseMove(pos)) return true;

    if (this.isVisible && this.containsPoint(pos)) {
      this.hoveredSlot = this.getSlotAtPosition(pos);
      return true;
    } else {
      this.hoveredSlot = -1;
    }
    return false;
  }

  private getSlotAtPosition(pos: Vector2): number {
    const startX = this.bounds.x + 15;
    const startY = this.bounds.y + this.titleBarHeight + 15;

    const relX = pos.x - startX;
    const relY = pos.y - startY;

    if (relX < 0 || relY < 0) return -1;

    const col = Math.floor(relX / (this.slotSize + 10));
    const row = Math.floor(relY / (this.slotSize + 10));

    if (col >= this.slotsPerRow) return -1;

    return row * this.slotsPerRow + col;
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const startX = this.bounds.x + 15;
    const startY = this.bounds.y + this.titleBarHeight + 15;

    // Render blueprint slots
    for (let i = 0; i < this.blueprints.length; i++) {
      const col = i % this.slotsPerRow;
      const row = Math.floor(i / this.slotsPerRow);
      const x = startX + col * (this.slotSize + 10);
      const y = startY + row * (this.slotSize + 10);

      const blueprint = this.blueprints[i];
      if (!blueprint) continue;
      const isHovered = i === this.hoveredSlot;
      const canBuild = this.canBuild(blueprint);

      // Slot background
      ctx.fillStyle = isHovered ? 'rgba(78, 204, 163, 0.3)' : 'rgba(50, 50, 70, 0.8)';
      ctx.fillRect(x, y, this.slotSize, this.slotSize);

      // Border
      ctx.strokeStyle = canBuild ? '#4ecca3' : '#555';
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.strokeRect(x, y, this.slotSize, this.slotSize);

      // Blueprint color preview
      ctx.fillStyle = canBuild ? blueprint.color : this.desaturate(blueprint.color);
      ctx.fillRect(x + 10, y + 10, this.slotSize - 20, this.slotSize - 30);

      // Blueprint name (localized)
      const structureInfo = Localization.getStructure(blueprint.id);
      const blueprintName = structureInfo?.name || blueprint.name;
      ctx.fillStyle = canBuild ? '#fff' : '#666';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      const shortName = blueprintName.length > 10 ? blueprintName.substring(0, 10) + '..' : blueprintName;
      ctx.fillText(shortName, x + this.slotSize / 2, y + this.slotSize - 5);
    }

    // Tooltip for hovered blueprint
    if (this.hoveredSlot >= 0 && this.hoveredSlot < this.blueprints.length) {
      const hoveredBlueprint = this.blueprints[this.hoveredSlot];
      if (hoveredBlueprint) {
        this.renderTooltip(ctx, hoveredBlueprint);
      }
    }
  }

  private canBuild(blueprint: Blueprint): boolean {
    if (!this.inventory) return false;
    for (const material of blueprint.materials) {
      if (!this.inventory.hasItem(material.itemId, material.quantity)) {
        return false;
      }
    }
    return true;
  }

  private renderTooltip(ctx: CanvasRenderingContext2D, blueprint: Blueprint): void {
    const tooltipX = this.bounds.x + this.bounds.width + 10;
    const tooltipY = this.bounds.y + this.titleBarHeight;
    const tooltipWidth = 180;
    const tooltipHeight = 100;

    // Get localized structure info
    const structureInfo = Localization.getStructure(blueprint.id);
    const blueprintName = structureInfo?.name || blueprint.name;
    const blueprintDesc = structureInfo?.description || blueprint.description;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.strokeStyle = '#4ecca3';
    ctx.lineWidth = 1;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(blueprintName, tooltipX + 10, tooltipY + 18);

    // Description (truncate if too long)
    ctx.fillStyle = '#aaa';
    ctx.font = '10px monospace';
    const maxDescLen = 28;
    const displayDesc = blueprintDesc.length > maxDescLen ? blueprintDesc.substring(0, maxDescLen - 2) + '..' : blueprintDesc;
    ctx.fillText(displayDesc, tooltipX + 10, tooltipY + 35);

    // Materials
    ctx.fillStyle = '#4ecca3';
    ctx.font = 'bold 10px monospace';
    ctx.fillText(t('build_cost'), tooltipX + 10, tooltipY + 55);

    let matY = tooltipY + 70;
    for (const material of blueprint.materials) {
      const itemInfo = Localization.getItem(material.itemId);
      const item = ItemRegistry.get(material.itemId);
      const itemName = itemInfo?.name || item?.name || material.itemId;
      const have = this.inventory?.getItemCount(material.itemId) ?? 0;
      const hasEnough = have >= material.quantity;

      ctx.fillStyle = hasEnough ? '#4ecca3' : '#e74c3c';
      ctx.font = '10px monospace';
      ctx.fillText(`${itemName}: ${have}/${material.quantity}`, tooltipX + 10, matY);
      matY += 14;
    }
  }

  private desaturate(color: string): string {
    // Simple desaturation by mixing with gray
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      const gray = (r + g + b) / 3;
      return `rgb(${Math.floor((r + gray) / 2)}, ${Math.floor((g + gray) / 2)}, ${Math.floor((b + gray) / 2)})`;
    }
    return color;
  }
}
